import { mapOpenWorldPreparation } from '@/api/adapters/supabase/open-world-preparations'
import { openWorldCanonError } from '@/api/adapters/supabase/open-world-errors'
import { mapOpenWorldNoteTurn } from '@/api/adapters/supabase/open-world-notes'
import { validOpenWorldConversationSettings } from '@/lib/openWorlds/conversation'
import { supabase } from '@/lib/supabaseClient'
import { requireOpenWorldSession } from '@/api/adapters/supabase/open-world-auth'
import {
  hasOpenWorldAttemptOwnKey,
  isOpenWorldAttemptNullableUuid,
  isOpenWorldAttemptPositiveInteger,
  isOpenWorldAttemptRecord,
  isOpenWorldAttemptTimestamp,
  isOpenWorldAttemptUuid,
  validateBeginOpenWorldAttemptRequest,
  validateCheckpointOpenWorldAttemptRequest,
  validateListOpenWorldAttemptsRequest,
  validateOpenWorldAttemptError,
  validateRecoverOpenWorldAttemptRequest,
  validateRenewOpenWorldAttemptLeaseRequest,
  validateTerminateOpenWorldAttemptRequest,
} from '@/lib/openWorlds/attemptPersistence'
import type {
  BeginOpenWorldAttemptRequest,
  BeginOpenWorldAttemptResponse,
  CheckpointOpenWorldAttemptRequest,
  CheckpointOpenWorldAttemptResponse,
  GetOpenWorldAttemptResponse,
  ListOpenWorldAttemptsRequest,
  ListOpenWorldAttemptsResponse,
  OpenWorldAttemptAcknowledgement,
  OpenWorldAttemptDetail,
  OpenWorldAttemptSummary,
  OpenWorldAttemptTerminalResponse,
  PlaythroughExecutionState,
  RecoverOpenWorldAttemptRequest,
  RenewOpenWorldAttemptLeaseRequest,
  RenewOpenWorldAttemptLeaseResponse,
  TerminateOpenWorldAttemptRequest,
} from '@/lib/openWorlds/attemptPersistence'
import {
  OpenWorldPersistenceError,
  validateOpenWorldDefinitionContent,
} from '@/lib/openWorlds/persistence'
import type {
  OpenWorldPersistenceErrorCode,
  OpenWorldValidationIssue,
} from '@/lib/openWorlds/persistence'

type UnknownRecord = Record<string, unknown>
const encoder = new TextEncoder()
const byteLength = (value: string): number => encoder.encode(value).byteLength

const ATTEMPT_SELECT = [
  'id',
  'playthrough_id',
  'parent_attempt_id',
  'base_head_id',
  'source_attempt_id',
  'intent',
  'turn_number',
  'claimed_revision',
  'user_message',
  'definition_revision',
  'configuration',
  'conversation_settings',
  'resolved_bot_ids',
  'status',
  'partial_text',
  'checkpoint_sequence',
  'lease_expires_at',
  'hard_deadline_at',
  'error',
  'created_at',
  'finished_at',
].join(', ')
const ATTEMPT_SUMMARY_SELECT = [
  'id',
  'playthrough_id',
  'parent_attempt_id',
  'base_head_id',
  'source_attempt_id',
  'intent',
  'turn_number',
  'claimed_revision',
  'user_message',
  'definition_revision',
  'status',
  'partial_text',
  'checkpoint_sequence',
  'lease_expires_at',
  'hard_deadline_at',
  'error',
  'created_at',
  'finished_at',
].join(', ')

function invalid(issues: Array<{ path: string; message: string }>): OpenWorldPersistenceError {
  return new OpenWorldPersistenceError('invalid_input', 'Open World attempt input is invalid.', {
    issues,
  })
}

function backend(path: string): never {
  throw new OpenWorldPersistenceError(
    'backend',
    `Malformed Open World attempt response at ${path}.`,
  )
}

function exact(value: unknown, keys: readonly string[], path: string): UnknownRecord {
  if (!isOpenWorldAttemptRecord(value)) backend(path)
  const actual = Object.keys(value)
  if (actual.length !== keys.length || keys.some((key) => !hasOpenWorldAttemptOwnKey(value, key)))
    backend(path)
  return value
}

function responseUuid(value: unknown, path: string): string {
  if (!isOpenWorldAttemptUuid(value)) backend(path)
  return value
}

function nullableResponseUuid(value: unknown, path: string): string | null {
  if (!isOpenWorldAttemptNullableUuid(value)) backend(path)
  return value
}

function responseInteger(value: unknown, path: string, allowZero = false): number {
  if (allowZero && value === 0) return 0
  if (!isOpenWorldAttemptPositiveInteger(value)) backend(path)
  return value
}

function responseTimestamp(value: unknown, path: string): string {
  if (!isOpenWorldAttemptTimestamp(value)) backend(path)
  return value
}

function nullableTimestamp(value: unknown, path: string): string | null {
  return value === null ? null : responseTimestamp(value, path)
}

function responseBoolean(value: unknown, path: string): boolean {
  if (typeof value !== 'boolean') backend(path)
  return value
}

function mapConfiguration(value: unknown, path: string) {
  const result = validateOpenWorldDefinitionContent({
    title: '',
    summary: '',
    sourceWorldId: null,
    configuration: value,
  })
  if (!result.success)
    throw new OpenWorldPersistenceError(
      'backend',
      `Malformed Open World attempt response at ${path}.`,
      { issues: result.issues },
    )
  return result.value.configuration
}

function mapResolvedBotIds(value: unknown, path: string) {
  const record = exact(value, ['storyteller', 'curator', 'synthesis'], path)
  return {
    storyteller: responseInteger(record.storyteller, `${path}.storyteller`),
    curator: responseInteger(record.curator, `${path}.curator`),
    synthesis: responseInteger(record.synthesis, `${path}.synthesis`),
  }
}

function mapError(value: unknown, path: string) {
  const result = validateOpenWorldAttemptError(value, path)
  if (!result.success) backend(path)
  return result.value
}

function mapAttempt(value: unknown, path = 'attempt', rpc = false): OpenWorldAttemptDetail {
  const keys = [
    'id',
    'playthroughId',
    'parentAttemptId',
    'baseHeadId',
    'sourceAttemptId',
    'intent',
    'turnNumber',
    'claimedRevision',
    'userMessage',
    'definitionRevision',
    'configuration',
    'resolvedBotIds',
    'status',
    'partialText',
    'checkpointSequence',
    'leaseExpiresAt',
    'hardDeadlineAt',
    'error',
    'createdAt',
    'finishedAt',
  ]
  const sqlKeys = [
    'id',
    'playthrough_id',
    'parent_attempt_id',
    'base_head_id',
    'source_attempt_id',
    'intent',
    'turn_number',
    'claimed_revision',
    'user_message',
    'definition_revision',
    'configuration',
    'resolved_bot_ids',
    'status',
    'partial_text',
    'checkpoint_sequence',
    'lease_expires_at',
    'hard_deadline_at',
    'error',
    'created_at',
    'finished_at',
  ]
  const settingsKey = rpc ? 'conversationSettings' : 'conversation_settings'
  if (isOpenWorldAttemptRecord(value) && settingsKey in value) (rpc ? keys : sqlKeys).push(settingsKey)
  const row = exact(value, rpc ? keys : sqlKeys, path)
  if (row[settingsKey] !== undefined && !validOpenWorldConversationSettings(row[settingsKey])) backend(`${path}.conversationSettings`)
  const get = (camel: string, snake: string) => row[rpc ? camel : snake]
  const intent = get('intent', 'intent')
  const status = get('status', 'status')
  if (!['advance', 'reroll', 'retry'].includes(intent as string)) backend(`${path}.intent`)
  if (!['running', 'completed', 'failed', 'cancelled', 'interrupted'].includes(status as string))
    backend(`${path}.status`)
  const userMessage = get('userMessage', 'user_message')
  const partialText = get('partialText', 'partial_text')
  if (typeof userMessage !== 'string') backend(`${path}.userMessage`)
  if (typeof partialText !== 'string') backend(`${path}.partialText`)
  if (!userMessage.trim() || byteLength(userMessage) > 64 * 1024) backend(`${path}.userMessage`)
  if (byteLength(partialText) > 256 * 1024) backend(`${path}.partialText`)
  const error = mapError(row.error, `${path}.error`)
  const finishedAt = nullableTimestamp(get('finishedAt', 'finished_at'), `${path}.finishedAt`)
  if (
    (status === 'running' && (error !== null || finishedAt !== null)) ||
    (status !== 'running' && finishedAt === null) ||
    (status === 'failed' && error === null) ||
    (status === 'completed' && error !== null)
  )
    backend(path)
  const leaseExpiresAt = responseTimestamp(
    get('leaseExpiresAt', 'lease_expires_at'),
    `${path}.leaseExpiresAt`,
  )
  const hardDeadlineAt = responseTimestamp(
    get('hardDeadlineAt', 'hard_deadline_at'),
    `${path}.hardDeadlineAt`,
  )
  if (Date.parse(leaseExpiresAt) > Date.parse(hardDeadlineAt)) backend(path)
  return {
    id: responseUuid(row.id, `${path}.id`),
    playthroughId: responseUuid(get('playthroughId', 'playthrough_id'), `${path}.playthroughId`),
    parentAttemptId: nullableResponseUuid(
      get('parentAttemptId', 'parent_attempt_id'),
      `${path}.parentAttemptId`,
    ),
    baseHeadId: nullableResponseUuid(get('baseHeadId', 'base_head_id'), `${path}.baseHeadId`),
    sourceAttemptId: nullableResponseUuid(
      get('sourceAttemptId', 'source_attempt_id'),
      `${path}.sourceAttemptId`,
    ),
    intent: intent as OpenWorldAttemptDetail['intent'],
    turnNumber: responseInteger(get('turnNumber', 'turn_number'), `${path}.turnNumber`),
    claimedRevision: responseInteger(
      get('claimedRevision', 'claimed_revision'),
      `${path}.claimedRevision`,
    ),
    userMessage,
    definitionRevision: responseInteger(
      get('definitionRevision', 'definition_revision'),
      `${path}.definitionRevision`,
    ),
    ...(row[settingsKey] === undefined ? {} : { conversationSettings: structuredClone(row[settingsKey]) }),
    configuration: mapConfiguration(row.configuration, `${path}.configuration`),
    resolvedBotIds: mapResolvedBotIds(
      get('resolvedBotIds', 'resolved_bot_ids'),
      `${path}.resolvedBotIds`,
    ),
    status: status as OpenWorldAttemptDetail['status'],
    partialText,
    checkpointSequence: responseInteger(
      get('checkpointSequence', 'checkpoint_sequence'),
      `${path}.checkpointSequence`,
      true,
    ),
    leaseExpiresAt,
    hardDeadlineAt,
    error,
    createdAt: responseTimestamp(get('createdAt', 'created_at'), `${path}.createdAt`),
    finishedAt,
  }
}

function mapSummary(value: unknown, path: string): OpenWorldAttemptSummary {
  const row = exact(
    value,
    [
      'id',
      'playthrough_id',
      'parent_attempt_id',
      'base_head_id',
      'source_attempt_id',
      'intent',
      'turn_number',
      'claimed_revision',
      'user_message',
      'definition_revision',
      'status',
      'partial_text',
      'checkpoint_sequence',
      'lease_expires_at',
      'hard_deadline_at',
      'error',
      'created_at',
      'finished_at',
    ],
    path,
  )
  const intent = row.intent
  const status = row.status
  if (!['advance', 'reroll', 'retry'].includes(intent as string)) backend(`${path}.intent`)
  if (!['running', 'completed', 'failed', 'cancelled', 'interrupted'].includes(status as string))
    backend(`${path}.status`)
  const finishedAt = nullableTimestamp(row.finished_at, `${path}.finishedAt`)
  const userMessage = row.user_message
  const partialText = row.partial_text
  if (typeof userMessage !== 'string') backend(`${path}.userMessage`)
  if (typeof partialText !== 'string') backend(`${path}.partialText`)
  const error = mapError(row.error, `${path}.error`)
  if (
    (status === 'running' && finishedAt !== null) ||
    (status !== 'running' && finishedAt === null)
  )
    backend(path)
  const leaseExpiresAt = responseTimestamp(row.lease_expires_at, `${path}.leaseExpiresAt`)
  const hardDeadlineAt = responseTimestamp(row.hard_deadline_at, `${path}.hardDeadlineAt`)
  if (Date.parse(leaseExpiresAt) > Date.parse(hardDeadlineAt)) backend(path)
  return {
    id: responseUuid(row.id, `${path}.id`),
    playthroughId: responseUuid(row.playthrough_id, `${path}.playthroughId`),
    parentAttemptId: nullableResponseUuid(row.parent_attempt_id, `${path}.parentAttemptId`),
    baseHeadId: nullableResponseUuid(row.base_head_id, `${path}.baseHeadId`),
    sourceAttemptId: nullableResponseUuid(row.source_attempt_id, `${path}.sourceAttemptId`),
    intent: intent as OpenWorldAttemptSummary['intent'],
    turnNumber: responseInteger(row.turn_number, `${path}.turnNumber`),
    claimedRevision: responseInteger(row.claimed_revision, `${path}.claimedRevision`),
    userMessage,
    definitionRevision: responseInteger(row.definition_revision, `${path}.definitionRevision`),
    status: status as OpenWorldAttemptSummary['status'],
    partialText,
    checkpointSequence: responseInteger(
      row.checkpoint_sequence,
      `${path}.checkpointSequence`,
      true,
    ),
    leaseExpiresAt,
    hardDeadlineAt,
    error,
    createdAt: responseTimestamp(row.created_at, `${path}.createdAt`),
    finishedAt,
  }
}

function mapState(value: unknown, path: string): PlaythroughExecutionState {
  const row = exact(
    value,
    ['id', 'revision', 'activeHeadId', 'pendingAttemptId', 'updatedAt'],
    path,
  )
  return {
    id: responseUuid(row.id, `${path}.id`),
    revision: responseInteger(row.revision, `${path}.revision`),
    activeHeadId: nullableResponseUuid(row.activeHeadId, `${path}.activeHeadId`),
    pendingAttemptId: nullableResponseUuid(row.pendingAttemptId, `${path}.pendingAttemptId`),
    updatedAt: responseTimestamp(row.updatedAt, `${path}.updatedAt`),
  }
}

function mapAcknowledgement(value: unknown): OpenWorldAttemptAcknowledgement {
  const row = exact(
    value,
    ['attemptId', 'status', 'finishedAt', 'playthroughRevision', 'activeHeadId'],
    'acknowledgement',
  )
  if (!['failed', 'cancelled', 'interrupted'].includes(row.status as string))
    backend('acknowledgement.status')
  return {
    attemptId: responseUuid(row.attemptId, 'acknowledgement.attemptId'),
    status: row.status as OpenWorldAttemptAcknowledgement['status'],
    finishedAt: responseTimestamp(row.finishedAt, 'acknowledgement.finishedAt'),
    playthroughRevision: responseInteger(
      row.playthroughRevision,
      'acknowledgement.playthroughRevision',
    ),
    activeHeadId: nullableResponseUuid(row.activeHeadId, 'acknowledgement.activeHeadId'),
  }
}

function parseIssues(details: unknown): OpenWorldValidationIssue[] {
  if (typeof details !== 'string') return []
  try {
    const value: unknown = JSON.parse(details)
    if (!isOpenWorldAttemptRecord(value) || !Array.isArray(value.issues)) return []
    return value.issues.flatMap((entry): OpenWorldValidationIssue[] =>
      isOpenWorldAttemptRecord(entry) &&
      typeof entry.path === 'string' &&
      typeof entry.message === 'string'
        ? [{ path: entry.path, message: entry.message }]
        : [],
    )
  } catch {
    return []
  }
}

function mapBackendError(error: unknown): OpenWorldPersistenceError {
  if (error instanceof OpenWorldPersistenceError) return error
  const record = isOpenWorldAttemptRecord(error) ? error : {}
  const message =
    typeof record.message === 'string' ? record.message : 'Open World attempt request failed.'
  const canonError = openWorldCanonError({ message })
  if (canonError) return canonError
  const match = /^OW_(UNAUTHENTICATED|NOT_FOUND|CONFLICT|INVALID_INPUT)$/.exec(message)
  if (record.code === 'P0001' && match) {
    const code = match[1]!.toLowerCase() as OpenWorldPersistenceErrorCode
    return new OpenWorldPersistenceError(code, message, {
      issues: code === 'invalid_input' ? parseIssues(record.details) : [],
      cause: error,
    })
  }
  return new OpenWorldPersistenceError('backend', message, { cause: error })
}

function checked<T>(
  result:
    | { success: true; value: T }
    | { success: false; issues: Array<{ path: string; message: string }> },
): T {
  if (!result.success) throw invalid(result.issues)
  return result.value
}

async function rpc(name: string, parameters: UnknownRecord): Promise<unknown> {
  await requireOpenWorldSession()
  const { data, error } = await supabase.rpc(name, parameters)
  if (error) throw mapBackendError(error)
  return data
}

export async function beginOpenWorldAttempt(
  request: BeginOpenWorldAttemptRequest,
): Promise<BeginOpenWorldAttemptResponse> {
  const value = checked(validateBeginOpenWorldAttemptRequest(request))
  const data = await rpc('rpc_begin_open_world_attempt_v5', {
    p_id: value.id,
    p_player_message_id: value.playerMessageId,
    p_playthrough_id: value.playthroughId,
    p_execution_token: value.executionToken,
    p_expected_playthrough_revision: value.expectedPlaythroughRevision,
    p_expected_head_id: value.expectedHeadId,
    p_expected_definition_revision: value.expectedDefinitionRevision,
    p_intent: value.intent,
    p_source_attempt_id: value.sourceAttemptId,
    p_user_message: value.userMessage,
  })
  const row = exact(data, ['attempt', 'playthroughState', 'replayed',
    ...(isOpenWorldAttemptRecord(data) && 'canonCaptured' in data ? ['canonCaptured'] : []),
    ...(isOpenWorldAttemptRecord(data) && 'noteTurn' in data ? ['noteTurn'] : []),
    ...(isOpenWorldAttemptRecord(data) && 'preparation' in data ? ['preparation'] : [])], 'response')
  return {
    ...(row.canonCaptured === undefined ? {} : { canonCaptured: responseBoolean(row.canonCaptured, 'response.canonCaptured') }),
    ...(row.noteTurn === undefined ? {} : { noteTurn: row.noteTurn === null ? null : mapOpenWorldNoteTurn(row.noteTurn) }),
    ...(row.preparation === undefined ? {} : {preparation: row.preparation === null ? null : mapOpenWorldPreparation(row.preparation)}),
    attempt: mapAttempt(row.attempt, 'attempt', true),
    playthroughState: mapState(row.playthroughState, 'playthroughState'),
    replayed: responseBoolean(row.replayed, 'response.replayed'),
  }
}

export async function renewOpenWorldAttemptLease(
  request: RenewOpenWorldAttemptLeaseRequest,
): Promise<RenewOpenWorldAttemptLeaseResponse> {
  const value = checked(validateRenewOpenWorldAttemptLeaseRequest(request))
  const row = exact(
    await rpc('rpc_renew_open_world_attempt_lease_v1', {
      p_attempt_id: value.attemptId,
      p_execution_token: value.executionToken,
    }),
    ['attemptId', 'leaseExpiresAt', 'hardDeadlineAt', 'serverNow'],
    'response',
  )
  return {
    attemptId: responseUuid(row.attemptId, 'response.attemptId'),
    leaseExpiresAt: responseTimestamp(row.leaseExpiresAt, 'response.leaseExpiresAt'),
    hardDeadlineAt: responseTimestamp(row.hardDeadlineAt, 'response.hardDeadlineAt'),
    serverNow: responseTimestamp(row.serverNow, 'response.serverNow'),
  }
}

export async function checkpointOpenWorldAttempt(
  request: CheckpointOpenWorldAttemptRequest,
): Promise<CheckpointOpenWorldAttemptResponse> {
  const value = checked(validateCheckpointOpenWorldAttemptRequest(request))
  const row = exact(
    await rpc('rpc_checkpoint_open_world_attempt_v1', {
      p_attempt_id: value.attemptId,
      p_execution_token: value.executionToken,
      p_sequence: value.sequence,
      p_partial_text: value.partialText,
    }),
    ['attemptId', 'sequence', 'replayed'],
    'response',
  )
  return {
    attemptId: responseUuid(row.attemptId, 'response.attemptId'),
    sequence: responseInteger(row.sequence, 'response.sequence'),
    replayed: responseBoolean(row.replayed, 'response.replayed'),
  }
}

export async function terminateOpenWorldAttempt(
  request: TerminateOpenWorldAttemptRequest,
): Promise<OpenWorldAttemptTerminalResponse> {
  const value = checked(validateTerminateOpenWorldAttemptRequest(request))
  return mapTerminal(
    await rpc('rpc_terminate_open_world_attempt_v1', {
      p_attempt_id: value.attemptId,
      p_execution_token: value.executionToken,
      p_receipt_id: value.receiptId,
      p_status: value.status,
      p_partial_text: value.partialText,
      p_error: value.error,
    }),
  )
}

export async function recoverOpenWorldAttempt(
  request: RecoverOpenWorldAttemptRequest,
): Promise<OpenWorldAttemptTerminalResponse> {
  const value = checked(validateRecoverOpenWorldAttemptRequest(request))
  return mapTerminal(
    await rpc('rpc_recover_open_world_attempt_v1', {
      p_attempt_id: value.attemptId,
      p_receipt_id: value.receiptId,
      p_expected_playthrough_revision: value.expectedPlaythroughRevision,
    }),
  )
}

function mapTerminal(value: unknown): OpenWorldAttemptTerminalResponse {
  const row = exact(value, ['acknowledgement', 'playthroughState', 'replayed'], 'response')
  return {
    acknowledgement: mapAcknowledgement(row.acknowledgement),
    playthroughState: mapState(row.playthroughState, 'playthroughState'),
    replayed: responseBoolean(row.replayed, 'response.replayed'),
  }
}

export async function getOpenWorldAttempt(id: string): Promise<GetOpenWorldAttemptResponse> {
  if (!isOpenWorldAttemptUuid(id)) throw invalid([{ path: 'id', message: 'Must be a UUID.' }])
  await requireOpenWorldSession()
  const { data, error } = await supabase
    .from('open_world_attempts')
    .select(ATTEMPT_SELECT)
    .eq('id', id)
    .maybeSingle()
  if (error) throw mapBackendError(error)
  return { attempt: data === null ? null : mapAttempt(data) }
}

export async function listOpenWorldAttempts(
  request: ListOpenWorldAttemptsRequest,
): Promise<ListOpenWorldAttemptsResponse> {
  const value = checked(validateListOpenWorldAttemptsRequest(request))
  await requireOpenWorldSession()
  let query = supabase
    .from('open_world_attempts')
    .select(ATTEMPT_SUMMARY_SELECT)
    .eq('playthrough_id', value.playthroughId)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(value.limit + 1)
  if (value.cursor)
    query = query.or(
      `created_at.lt.${value.cursor.createdAt},and(created_at.eq.${value.cursor.createdAt},id.lt.${value.cursor.id})`,
    )
  const { data, error } = await query
  if (error) throw mapBackendError(error)
  if (!Array.isArray(data)) backend('items')
  const mapped = data.map((row, index) => mapSummary(row, `items[${index}]`))
  const items = mapped.slice(0, value.limit)
  const last = items.at(-1)
  return {
    items,
    nextCursor:
      mapped.length > value.limit && last ? { createdAt: last.createdAt, id: last.id } : null,
  }
}
